import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/auth";

function StatusPill({ status }) {
  const styles = {
    active: "bg-[#E8F5E9] text-[#1FA868]",
    pending: "bg-[#FFF5D6] text-amber-700",
    cancelled: "bg-rose-50 text-rose-600",
  };

  return (
    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-md ${styles[status] || "bg-black/5 text-black/60"}`}>
      {status}
    </span>
  );
}

export default function AdminSubscriptions() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/admin/subscriptions").then((r) => setRows(r.data));
  }, []);

  // Quick stats
  const total = rows.length;
  const active = rows.filter((r) => r.status === "active").length;
  const revenue = rows.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="p-8 lg:p-10 max-w-[1400px] space-y-6">

      {/* 🔥 Header */}
      <div>
        <h1 className="font-display font-extrabold text-4xl text-black tracking-tight">
          Subscriptions
        </h1>
        <p className="text-black/60 mt-1 text-sm">
          Monitor all tenant subscriptions across your platform.
        </p>
      </div>

      {/* 📊 Stats Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#F4F8F3] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Total Subscriptions</div>
          <div className="text-2xl font-bold text-black mt-1">{total}</div>
        </div>

        <div className="bg-[#EEF6FF] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Active</div>
          <div className="text-2xl font-bold text-[#1FA868] mt-1">{active}</div>
        </div>

        <div className="bg-[#FFF5D6] rounded-2xl p-5 border border-black/5">
          <div className="text-xs text-black/50">Total Revenue</div>
          <div className="text-2xl font-bold text-black mt-1">
            ₦{revenue.toLocaleString()}
          </div>
        </div>
      </div>

      {/* 📋 Table */}
      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden shadow-sm">

        {/* Table header */}
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between">
          <h2 className="font-semibold text-black">All Subscriptions</h2>
          <span className="text-xs text-black/50">{rows.length} records</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-black/55 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-6 py-3 font-bold">Tenant</th>
                <th className="text-left px-6 py-3 font-bold">Subdomain</th>
                <th className="text-left px-6 py-3 font-bold">Plan</th>
                <th className="text-left px-6 py-3 font-bold">Amount</th>
                <th className="text-left px-6 py-3 font-bold">Cycle</th>
                <th className="text-left px-6 py-3 font-bold">Status</th>
                <th className="text-left px-6 py-3 font-bold">Started</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-black/5">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-[#F9FAFB] transition"
                >
                  <td className="px-6 py-4">
                    <Link
                      to={`/admin/tenants/${r.id}`}
                      className="font-semibold text-black hover:text-[#1FA868]"
                    >
                      {r.tenant}
                    </Link>
                  </td>

                  <td className="px-6 py-4 font-mono text-xs text-black/60">
                    {r.subdomain}
                  </td>

                  <td className="px-6 py-4 text-black/70">
                    {r.plan}
                  </td>

                  <td className="px-6 py-4 font-semibold tabular-nums">
                    ₦{Number(r.amount || 0).toLocaleString()}
                  </td>

                  <td className="px-6 py-4 text-black/70 capitalize">
                    {r.billing_cycle}
                  </td>

                  <td className="px-6 py-4">
                    <StatusPill status={r.status} />
                  </td>

                  <td className="px-6 py-4 text-black/50 text-xs">
                    {(r.created_at || "").slice(0, 10)}
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-12 text-center text-black/50">
                    No subscriptions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}